import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, Eye, X, MousePointerClick, MessageSquare } from "lucide-react";
import { format } from "date-fns";

interface AlertLog {
  id: string;
  user_id: string;
  session_id: string | null;
  action: "shown" | "dismissed" | "cta_clicked" | "toast";
  month: number;
  year: number;
  overdue_count: number;
  missing_count: number;
  total_count: number;
  metadata: any;
  user_agent: string | null;
  created_at: string;
}

const ACTION_META: Record<AlertLog["action"], { label: string; icon: any; cls: string }> = {
  shown: { label: "عُرض البانر", icon: Eye, cls: "bg-blue-100 text-blue-700 border-blue-300" },
  toast: { label: "إشعار سريع", icon: MessageSquare, cls: "bg-amber-100 text-amber-700 border-amber-300" },
  cta_clicked: { label: "فتح حالة الشهر", icon: MousePointerClick, cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  dismissed: { label: "تم الإغلاق", icon: X, cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

const AccountantAlertsLog = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["subscription-alert-logs"],
    queryFn: async () => {
      const [{ data: logs }, { data: profiles }] = await Promise.all([
        supabase
          .from("subscription_alert_logs" as any)
          .select("*")
          .order("created_at", { ascending: false })
          .limit(500),
        supabase.from("profiles").select("user_id, full_name"),
      ]);
      const nameMap = new Map<string, string>(
        ((profiles || []) as any[]).map((p) => [p.user_id, p.full_name || ""]),
      );
      return ((logs || []) as unknown as AlertLog[]).map((l) => ({
        ...l,
        user_name: nameMap.get(l.user_id) || l.user_id.slice(0, 8),
      }));
    },
  });

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-amber-600" />
        <h1 className="text-xl font-bold">سجل تنبيهات فواتير الاشتراكات</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        كل تنبيه يظهر للإدارة/المحاسب يُسجَّل هنا مع المستخدم وجلسته ووقت الإغلاق أو فتح حالة الشهر.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">آخر 500 حدث</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">جاري التحميل…</p>
          ) : !data || data.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">لا توجد سجلات بعد.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="p-2 text-right">التاريخ</th>
                    <th className="p-2 text-right">المستخدم</th>
                    <th className="p-2 text-right">الحدث</th>
                    <th className="p-2 text-right">الشهر</th>
                    <th className="p-2 text-right">متأخرة</th>
                    <th className="p-2 text-right">بدون فاتورة</th>
                    <th className="p-2 text-right">الجلسة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((l: any) => {
                    const meta = ACTION_META[l.action as AlertLog["action"]];
                    const Icon = meta?.icon ?? Bell;
                    return (
                      <tr key={l.id} className="border-t hover:bg-muted/30">
                        <td className="p-2 whitespace-nowrap text-xs">
                          {format(new Date(l.created_at), "yyyy-MM-dd HH:mm:ss")}
                        </td>
                        <td className="p-2">{l.user_name}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={`gap-1 ${meta?.cls ?? ""}`}>
                            <Icon className="w-3 h-3" />
                            {meta?.label ?? l.action}
                          </Badge>
                        </td>
                        <td className="p-2 whitespace-nowrap">{l.month}/{l.year}</td>
                        <td className="p-2 text-red-600">{l.overdue_count}</td>
                        <td className="p-2">{l.missing_count}</td>
                        <td className="p-2 text-xs text-muted-foreground" title={l.session_id || ""}>
                          {l.session_id ? l.session_id.slice(0, 10) + "…" : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantAlertsLog;
